import { backendTime } from "./dates"

// The Workflow view's three banker steps and its "Do this now" card (DESIGN-LOG §24). One pure reading of what
// Bobur's panel and the report already hold, so a ✓ always means "done and OK" and never just "a saved result exists".
//
// Steps, each named for what the RM does: Data files, Update the report, Review.
// Status grammar: done (green check), todo (empty circle), attention (orange, needs you), running (spinner),
// waiting (dashed, an earlier step must finish first). The one primary on screen is the card's.

export type StepID = "files" | "update" | "review"
export type StepStatus = "done" | "todo" | "attention" | "running" | "waiting"
export type StepSummary = { key: string; count?: number; date?: string }
export type WorkflowStep = { id: StepID; status: StepStatus; summary: StepSummary }
export type ReviewState = "none" | "requested" | "approved" | "rejected"

export type WorkflowFacts = {
  // Files of this period that the report can read (his files list, scoped to the period, no case).
  files: { count: number; ready: number; latest?: string; error?: boolean }
  uploading: boolean
  // Tables: the report's own category definitions, and the categories the assistant has read (his applied rows).
  tables: { defined: number; read: number; failed: number; inferred: boolean }
  // reportRunRequirements() issues for the report's own run action.
  requirements: readonly string[]
  // His saved extraction and transformation records for this period.
  processing: { extracted: boolean; transformed: boolean; failed: boolean; at?: string }
  // The report's latest real run (not a check).
  run?: { status: string; created_at: string }
  // A run is going on now (his live status, or the report's active run), and which stage it is at.
  running?: "tables" | "processing" | "other"
  stale: boolean
  // This client's tables in the latest output (filtered by target).
  client: { tables: number; at?: string }
  review?: ReviewState
  // §74b: whether this viewer has looked at the report (Preview or the Editor) since its latest update; false only when
  // known unseen (unknown keeps the review card).
  seen?: boolean
}

// The card at the top of the detail: one sentence, at most one primary, and the step it points to.
export type CardState =
  | "upload" // no files: Upload files
  | "uploading" // files going up: spinner, no button
  | "filesError" // the files list failed to load: Try again (reload), not Upload
  | "processing" // a file is in but not ready yet: spinner, no button
  | "setup" // the report misses a setting the run needs: Set up
  | "run" // never run (reason first), files newer than the last update (files), or stale (settings): Run
  | "running" // an update is going on: spinner, no button
  | "failed" // the last update failed: Try again, plus Technical details
  | "noClient" // the update ran but holds no tables for this client: Check report settings
  | "lookOver" // data up to date, not looked at since the update: Open report (Request review quiet after it)
  | "requestReview" // data up to date, review not requested: Request review
  | "waitingReview" // review requested: no primary
  | "changes" // changes requested: Open review
  | "approved" // approved: Share
export type CardAction = "upload" | "reload" | "setup" | "run" | "retry" | "settings" | "openReport" | "requestReview" | "openReview" | "share"
// Why a run is due: never updated, files newer than the last update, or the report's settings changed since (stale).
export type RunReason = "first" | "files" | "settings"
export type WorkflowCard = { state: CardState; step: StepID; action?: CardAction; reason?: RunReason }

// A run the person stopped (cancelled) is no failure: the card offers the run again, not Try again (code review).
const FAILED = ["failed", "error", "interrupted"]

export function workflowSteps(facts: WorkflowFacts): WorkflowStep[] {
  const files = filesStep(facts)
  const update = updateStep(facts, files)
  return [files, update, reviewStep(facts, update)]
}

// What the RM should do now. Read from the same rules as the step statuses, first open step first.
export function workflowCard(facts: WorkflowFacts): WorkflowCard {
  if (facts.uploading) return { state: "uploading", step: "files" }
  if (facts.files.error) return { state: "filesError", step: "files", action: "reload" }
  if (!facts.files.count) return { state: "upload", step: "files", action: "upload" }
  if (facts.files.ready < facts.files.count) return { state: "processing", step: "files" }
  if (facts.running) return { state: "running", step: "update" }
  const issue = setupIssue(facts)
  if (issue) return { state: "setup", step: "update", action: "setup" }
  if (updateFailed(facts)) return { state: "failed", step: "update", action: "retry" }
  const reason = runReason(facts)
  if (reason) return { state: "run", step: "update", action: "run", reason }
  if (!facts.client.tables) return { state: "noClient", step: "update", action: "settings" }
  if (facts.review === "approved") return { state: "approved", step: "review", action: "share" }
  if (facts.review === "rejected") return { state: "changes", step: "review", action: "openReview" }
  if (facts.review === "requested") return { state: "waitingReview", step: "review" }
  // Up to date and not reviewed: look at what the update filled first (Artyom: «я даже не видел ещё отчёта»).
  if (facts.seen === false) return { state: "lookOver", step: "update", action: "openReport" }
  return { state: "requestReview", step: "review", action: "requestReview" }
}

// §74g: which run Workflow's Update step shows as its task list. A run going, whatever the card says (an upload or a file
// getting ready while it runs); the last run, failed, while the card says so; a run this view saw going that ended before
// his status did (the card still says running), as its Done line.
export function shownRun<R extends { id: string; status: string }>(run: R | undefined, card: CardState | undefined, watched: string, state: (status: string) => "running" | "done" | "failed" | "stopped") {
  if (!run) return undefined
  const value = state(run.status)
  if (value === "running") return run
  if (value === "failed") return card === "failed" ? run : undefined
  return card === "running" && run.id === watched ? run : undefined
}

// The report's latest update: when its newest completed calculation was saved (the Update step's "Last updated", and the
// time a look at the report is stamped with, §74b).
export function latestUpdate(records: readonly { status: string; updated_at?: string | null; created_at: string }[]) {
  return records.filter((record) => record.status === "completed").map((record) => record.updated_at || record.created_at).sort((a, b) => a.localeCompare(b)).at(-1)
}

// §74g: this report's own latest update (its calculations, by output_json.workspace_id, as the editor's tables are), so
// another report's run in the period never makes it "unseen" or stamps its time on it.
export function reportUpdate(records: readonly { status: string; updated_at?: string | null; created_at: string; case_id?: string | null; output_json?: Record<string, unknown> }[], reportID: string) {
  return latestUpdate(records.filter((record) => record.output_json?.workspace_id === reportID && !record.case_id))
}

// §74j: the report's latest run (its own, newest first, as Workflow lists them) ended in a failure: not stopped by the
// person, not being resumed. The view switch marks Workflow with it (Artyom: «если есть ошибка надо показывать это в
// табах, а то ни одного сигнала»), where the failure is told.
export function lastRunFailed(runs: readonly { status: string; check_only?: boolean }[]) {
  const run = runs.find((item) => !item.check_only)
  return !!run && (run.status === "failed" || run.status === "error")
}

// A setting the report's run needs and that the RM can fix (files are the Files step's): a table without fields or no
// tables when the report defines its own, no period, no named client.
export function setupIssue(facts: WorkflowFacts) {
  return facts.requirements.find((issue) => issue !== "files" && issue !== "loading")
}

// Any failure since the last good result: the latest run, the latest read or calculation, or a table that failed to read.
function updateFailed(facts: WorkflowFacts) {
  return (!!facts.run && FAILED.includes(facts.run.status)) || facts.processing.failed || facts.tables.failed > 0
}

// Never run, or the data is older than the files: the report says stale, or a file arrived after the last run started
// (after the newest calculation when the report has no run of its own).
export function needsRun(facts: WorkflowFacts) {
  return !!runReason(facts)
}

// The card's sentence for Run: "Not updated yet", "New files since <date>", "Settings changed since the last update".
export function runReason(facts: WorkflowFacts): RunReason | undefined {
  if (!facts.processing.extracted || !facts.processing.transformed) return "first"
  if (backendTime(facts.files.latest) > backendTime(facts.run?.created_at ?? facts.processing.at)) return "files"
  if (facts.stale) return "settings"
}

function filesStep(facts: WorkflowFacts): WorkflowStep {
  const value = facts.files
  if (facts.uploading) return { id: "files", status: "running", summary: { key: "datox.v2.workflow.files.uploading" } }
  if (value.error) return { id: "files", status: "attention", summary: { key: "datox.v2.workflow.files.error" } }
  if (!value.count) return { id: "files", status: "attention", summary: { key: "datox.v2.workflow.files.none" } }
  if (value.ready < value.count) return { id: "files", status: "running", summary: { key: "datox.v2.workflow.files.notReady", count: value.count - value.ready } }
  return { id: "files", status: "done", summary: { key: "datox.v2.workflow.files.done", count: value.count, date: value.latest } }
}

// ✓ only when the last update completed, nothing failed since, it is not older than the files, and it filled this client.
function updateStep(facts: WorkflowFacts, files: WorkflowStep): WorkflowStep {
  if (files.status !== "done") return { id: "update", status: "waiting", summary: { key: "datox.v2.workflow.update.waiting" } }
  if (facts.running) return { id: "update", status: "running", summary: { key: "datox.v2.workflow.update.running" } }
  if (setupIssue(facts)) return { id: "update", status: "attention", summary: { key: "datox.v2.workflow.update.setup" } }
  if (updateFailed(facts)) return { id: "update", status: "attention", summary: { key: "datox.v2.workflow.update.failed", date: facts.run?.created_at } }
  if (!facts.processing.extracted || !facts.processing.transformed) return { id: "update", status: "todo", summary: { key: "datox.v2.workflow.update.never" } }
  const reason = runReason(facts)
  if (reason) return { id: "update", status: "todo", summary: { key: reason === "settings" ? "datox.v2.workflow.update.settingsChanged" : "datox.v2.workflow.update.newFiles" } }
  if (!facts.client.tables) return { id: "update", status: "attention", summary: { key: "datox.v2.workflow.update.noClient" } }
  return { id: "update", status: "done", summary: { key: "datox.v2.workflow.update.done", date: facts.processing.at ?? facts.run?.created_at } }
}

function reviewStep(facts: WorkflowFacts, update: WorkflowStep): WorkflowStep {
  if (update.status !== "done") return { id: "review", status: "waiting", summary: { key: "datox.v2.workflow.review.waiting" } }
  if (facts.review === "approved") return { id: "review", status: "done", summary: { key: "datox.v2.workflow.review.approved" } }
  if (facts.review === "rejected") return { id: "review", status: "attention", summary: { key: "datox.v2.workflow.review.rejected" } }
  if (facts.review === "requested") return { id: "review", status: "todo", summary: { key: "datox.v2.workflow.review.requested" } }
  return { id: "review", status: "todo", summary: { key: "datox.v2.workflow.review.none" } }
}

// Data files (W17): his upload adds to the period, so last month's exports stay next to this month's. Files of one kind
// share a name once the copy suffix ("(2)"), the extension and any date or month in it are dropped; the newest of each
// kind is current, the others are "previous". Returns the ids of the previous ones.
export function previousFiles(files: readonly { id: string; filename: string; created_at: string }[]) {
  const newest = new Map<string, { id: string; at: number }>()
  for (const file of files) {
    const kind = fileKind(file.filename)
    const at = backendTime(file.created_at)
    const current = newest.get(kind)
    if (!current || at > current.at) newest.set(kind, { id: file.id, at })
  }
  return new Set(files.filter((file) => newest.get(fileKind(file.filename))?.id !== file.id).map((file) => file.id))
}

const MONTHS = "jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?"
export function fileKind(filename: string) {
  return filename.toLowerCase()
    .replace(/\.[a-z0-9]{1,5}$/, "")
    .replace(/\s*\(\d+\)$/, "")
    .replace(/[\s_\-.]+/g, " ")
    .replace(/\d{4} ?\d{2}(?: ?\d{2})?|\d{2} \d{2} \d{4}/g, " ")
    .replace(new RegExp(`\\b(?:${MONTHS})\\b`, "g"), " ")
    .replace(/\b(?:19|20)\d{2}\b/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}
